import { useState } from 'react';
import { useApp, ROLES, ROLE_LABELS } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { defaultProducts, defaultVerificationTypes, defaultLeadCreationQuestions } from '../../data/mockData';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import {
  Search,
  Bell,
  Plus,
  ChevronDown,
  RefreshCw,
  User,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  IdCard,
  FileCheck,
  Download,
  Eye,
  Shield,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export const Header = () => {
  const { 
    currentUserId,
    currentRole, 
    setCurrentRole, 
    setCurrentUserId,
    unreadCount, 
    notifications,
    markNotificationRead,
    sidebarCollapsed,
    logout,
    hasPermission
  } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showIdPreviewModal, setShowIdPreviewModal] = useState(false);
  const [leadStep, setLeadStep] = useState(1);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVerificationTypeId, setSelectedVerificationTypeId] = useState('');
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [leadClient, setLeadClient] = useState('HDFC Bank');
  const [leadPartnerCode, setLeadPartnerCode] = useState('');

  const storedUser = (() => {
    try {
      const u = localStorage.getItem('crm_user');
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();
  const activeUser = storedUser || {
    id: currentUserId || 'USR001',
    name: 'Rajesh Kumar',
    email: 'rajesh.kumar@adventures.com',
    role: currentRole || 'admin',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'
  };

  const canSeeQuickAction = [
    ROLES.ADMIN, 
    ROLES.SUPREME_MANAGER, 
    ROLES.CLIENT, 
    ROLES.DATA_ENTRY_OPERATOR
  ].includes(currentRole) && hasPermission('cases', 'create');

  const handleResetLeadWizard = () => {
    setLeadStep(1);
    setSelectedProductId('');
    setSelectedVerificationTypeId('');
    setDynamicAnswers({});
    setLeadPartnerCode('');
    setShowLeadModal(false);
  };

  const handleSaveLeadDraft = () => {
    if (!hasPermission('cases', 'create') || currentRole === ROLES.VENDOR) {
      toast.error('You do not have permission to save lead drafts.');
      return;
    }
    toast.success('Lead draft saved successfully! You can resume this case later.');
    handleResetLeadWizard();
  };

  const handleSubmitLeadCase = () => {
    if (!hasPermission('cases', 'create') || currentRole === ROLES.VENDOR) {
      toast.error('You do not have permission to generate cases.');
      return;
    }
    toast.success('Dynamic Lead Form validated and Case generated successfully!');
    handleResetLeadWizard();
  };

  return (
    <header className="crm-header" data-testid="header">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-slate-500 hover:text-slate-900"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </Button>

        {/* Search */}
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="text"
            placeholder="Search cases, clients, users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-80 pl-10 bg-white border-slate-200 focus:border-blue-500/50 text-sm"
            data-testid="global-search"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        <div className="font-bold text-black text-lg mx-2 tracking-tight">AD Ventures</div>
        
        {/* Role Badge */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700">
          <Shield className="w-3.5 h-3.5 text-blue-600" />
          <span>{ROLE_LABELS[currentRole] || 'Admin'}</span>
        </div>

        {/* Quick Actions - New Case Modal Trigger */}
        {canSeeQuickAction && (
          <Button
            variant="default"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="quick-actions-btn"
            onClick={() => setShowLeadModal(true)}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Case / Lead</span>
          </Button>
        )}

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 bg-white border-slate-200">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span className="text-slate-900">Notifications</span>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="bg-blue-50 text-blue-600 text-xs">
                  {unreadCount} new
                </Badge>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-slate-100" />
            {notifications.slice(0, 5).map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                onSelect={(e) => {
                  e.preventDefault();
                  markNotificationRead(notification.id);
                }}
                className={cn(
                  'flex flex-col items-start gap-1 cursor-pointer py-3',
                  !notification.read && 'bg-blue-50'
                )}
              >
                <div className="flex items-center gap-2">
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-blue-600" />
                  )}
                  <span className="font-medium text-sm">{notification.title}</span>
                </div>
                <span className="text-xs text-slate-500 line-clamp-1">{notification.message}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-slate-100" />
            <DropdownMenuItem className="text-center text-blue-600 cursor-pointer justify-center">
              View all notifications
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="gap-2 px-2 hover:bg-slate-100"
              data-testid="user-menu-btn"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={activeUser.avatar} />
                <AvatarFallback>{activeUser.name ? activeUser.name.split(' ').map(n => n[0]).join('') : 'RK'}</AvatarFallback>
              </Avatar>
              <ChevronDown className="w-4 h-4 text-slate-500 hidden sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-slate-900">{activeUser.name}</span>
                <span className="text-xs text-slate-500 font-normal">{activeUser.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-slate-100" />
            <DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => setShowProfileModal(true)}>
              <User className="w-4 h-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer gap-2">
              <HelpCircle className="w-4 h-4" />
              Help & Support
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-100" />
            <DropdownMenuItem className="cursor-pointer gap-2 text-red-400" onSelect={logout}>
              <LogOut className="w-4 h-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Adaptive New Lead Modal - Upgraded to a 4-step Dynamic Wizard */}
      <Dialog open={showLeadModal} onOpenChange={(open) => { if(!open) handleResetLeadWizard(); else setShowLeadModal(true); }}>
        <DialogContent className="bg-white border border-slate-200 text-slate-800 sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
          <DialogHeader className="border-b border-slate-100 pb-3">
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Create New Lead / Case Wizard
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs font-medium">
              Step {leadStep} of 4 • Configure dynamic verification attributes and details
            </DialogDescription>
          </DialogHeader>

          {/* Wizard Step Progress Tracker */}
          <div className="flex items-center gap-1.5 py-2">
            {[1, 2, 3, 4].map(s => (
              <div 
                key={s} 
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  leadStep >= s ? 'bg-blue-600' : 'bg-slate-100'
                }`}
              />
            ))}
          </div>

          <div className="py-3">
            {/* STEP 1: Select Product */}
            {leadStep === 1 && (
              <div className="space-y-4">
                <Label className="text-xs font-bold text-slate-700 block mb-1">Step 1: Select Target Loan Product</Label>
                <div className="max-h-[250px] overflow-y-auto space-y-2 pr-1 scrollbar-thin">
                  {defaultProducts.map(p => {
                    const isSelected = selectedProductId === p.id;
                    return (
                      <div
                        key={p.id}
                        onClick={() => setSelectedProductId(p.id)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer flex justify-between items-center ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' 
                            : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <div>
                          <p className="text-sm font-medium">{p.name}</p>
                          <p className="text-xs opacity-80">Code: {p.code}</p>
                        </div>
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 2: Select Verification Type */}
            {leadStep === 2 && (
              <div className="space-y-4">
                <Label className="text-xs font-bold text-slate-700 block mb-1">Step 2: Select Verification Type</Label>
                <div className="max-h-[250px] overflow-y-auto space-y-2 pr-1 scrollbar-thin">
                  {defaultVerificationTypes.map(vt => {
                    const isSelected = selectedVerificationTypeId === vt.id;
                    return (
                      <div
                        key={vt.id}
                        onClick={() => setSelectedVerificationTypeId(vt.id)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer flex justify-between items-center ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' 
                            : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <p className="text-sm font-medium">{vt.name}</p>
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 3: Dynamic Form Renderer */}
            {leadStep === 3 && (
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <Label className="text-xs font-bold text-slate-700">Step 3: Dynamic Field Information</Label>
                  <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-semibold uppercase">
                    Verification Mapped Form
                  </span>
                </div>
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-1">
                  {/* Client Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-600">Assign Client</Label>
                      <Select value={leadClient} onValueChange={setLeadClient}>
                        <SelectTrigger className="w-full bg-white border-slate-200 text-slate-800 text-xs">
                          <SelectValue placeholder="Select Client" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 text-slate-800">
                          <SelectItem value="HDFC Bank">HDFC Bank</SelectItem>
                          <SelectItem value="ICICI Bank">ICICI Bank</SelectItem>
                          <SelectItem value="Bajaj Finance">Bajaj Finance</SelectItem>
                          <SelectItem value="Axis Bank">Axis Bank</SelectItem>
                          <SelectItem value="Kotak Mahindra">Kotak Mahindra</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-600">Partner Code (Optional)</Label>
                      <Input
                        type="text"
                        placeholder="e.g. BP092"
                        value={leadPartnerCode}
                        onChange={e => setLeadPartnerCode(e.target.value)}
                        className="bg-white border-slate-200 text-slate-800 text-xs h-9"
                      />
                    </div>
                  </div>

                  {/* Render dynamic questions */}
                  {defaultLeadCreationQuestions
                    .filter(q => q.verificationTypeId === selectedVerificationTypeId || (selectedVerificationTypeId === 'VT001' ? q.verificationTypeId === 'VT001' : true))
                    .map(q => (
                      <div key={q.id} className="space-y-1.5">
                        <Label className="text-xs font-medium text-slate-600">
                          {q.fieldLabel} {q.required && <span className="text-red-500">*</span>}
                        </Label>
                        {q.fieldType === 'textarea' ? (
                          <textarea
                            className="w-full p-2.5 rounded-lg border border-slate-200 bg-white text-slate-800 text-xs min-h-[60px] focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                            placeholder={`Enter ${q.fieldLabel}`}
                            value={dynamicAnswers[q.id] || ''}
                            onChange={e => setDynamicAnswers({ ...dynamicAnswers, [q.id]: e.target.value })}
                          />
                        ) : (
                          <Input
                            type={q.fieldType === 'number' ? 'number' : 'text'}
                            className="bg-white border-slate-200 text-slate-800 text-xs h-9"
                            placeholder={`Enter ${q.fieldLabel}`}
                            value={dynamicAnswers[q.id] || ''}
                            onChange={e => setDynamicAnswers({ ...dynamicAnswers, [q.id]: e.target.value })}
                          />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* STEP 4: Summary & Submit */}
            {leadStep === 4 && (
              <div className="space-y-4">
                <Label className="text-xs font-bold text-slate-700 block mb-1">Step 4: Review and Generate Case</Label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3.5 text-xs text-slate-700">
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-200/50">
                    <span className="font-semibold text-slate-500">Assigned Client</span>
                    <span className="font-bold text-slate-800">{leadClient}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-200/50">
                    <span className="font-semibold text-slate-500">Selected Product</span>
                    <span className="font-bold text-slate-800">
                      {defaultProducts.find(p => p.id === selectedProductId)?.name || 'None'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-200/50">
                    <span className="font-semibold text-slate-500">Verification Type</span>
                    <span className="font-bold text-slate-800">
                      {defaultVerificationTypes.find(vt => vt.id === selectedVerificationTypeId)?.name || 'None'}
                    </span>
                  </div>
                  {leadPartnerCode && (
                    <div className="flex justify-between items-center py-1.5 border-b border-slate-200/50">
                      <span className="font-semibold text-slate-500">Partner Code</span>
                      <span className="font-bold text-slate-800">{leadPartnerCode}</span>
                    </div>
                  )}

                  <div className="pt-2">
                    <p className="font-bold text-slate-800 mb-2 border-b border-slate-200 pb-1">Answers Summary</p>
                    <div className="max-h-[150px] overflow-y-auto space-y-1.5 pr-1">
                      {defaultLeadCreationQuestions
                        .filter(q => q.verificationTypeId === selectedVerificationTypeId || (selectedVerificationTypeId === 'VT001' ? q.verificationTypeId === 'VT001' : true))
                        .map(q => (
                          <div key={q.id} className="flex justify-between gap-4 py-0.5">
                            <span className="text-slate-500">{q.fieldLabel}:</span>
                            <span className="text-slate-800 font-medium truncate max-w-[200px]">
                              {dynamicAnswers[q.id] || <span className="italic text-slate-400">Not filled</span>}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-slate-100 pt-3 mt-4 flex justify-between items-center">
            <div>
              {leadStep > 1 && (
                <Button
                  variant="ghost"
                  onClick={() => setLeadStep(prev => prev - 1)}
                  className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                >
                  Back
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              {leadStep < 4 ? (
                <Button
                  onClick={() => {
                    if (leadStep === 1 && !selectedProductId) {
                      toast.error('Select a product to continue');
                      return;
                    }
                    if (leadStep === 2 && !selectedVerificationTypeId) {
                      toast.error('Select a verification type to continue');
                      return;
                    }
                    setLeadStep(prev => prev + 1);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Continue
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={handleSaveLeadDraft}
                    className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    Save Draft
                  </Button>
                  <Button 
                    onClick={handleSubmitLeadCase}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Submit Lead Case
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Profile & Credentials Modal for FE and Vendor */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 sm:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
          <DialogHeader className="border-b border-slate-100 pb-3">
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Profile & Official Credentials
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-6">
            {/* Header info */}
            <div className="flex flex-col sm:flex-row items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
              <Avatar className="w-16 h-16 border-2 border-white shadow-md bg-slate-100">
                <AvatarImage src={activeUser.avatar} className="object-cover" />
                <AvatarFallback className="text-2xl text-slate-400">{activeUser.name?.split(' ').map(n => n[0]).join('') || 'U'}</AvatarFallback>
              </Avatar>
              <div className="text-center sm:text-left space-y-1">
                <h3 className="text-lg font-bold text-slate-900 leading-tight">{activeUser.name}</h3>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{ROLE_LABELS[currentRole]}</p>
                <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-1.5 text-xs text-slate-500 font-medium">
                  <span>ID: {activeUser.id}</span>
                  <span>•</span>
                  <span>{activeUser.email}</span>
                  <span>•</span>
                  <span>{activeUser.phone || '+91 99999 99999'}</span>
                </div>
              </div>
            </div>

            {/* Credentials Row for FE and Vendor */}
            {(currentRole === ROLES.FIELD_EXECUTIVE || currentRole === ROLES.VENDOR) && (
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider">My Credential Documents</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* FE ID Card */}
                  {currentRole === ROLES.FIELD_EXECUTIVE ? (
                    <div className="p-5 border border-slate-200 rounded-2xl bg-white shadow-xs flex flex-col justify-between h-[280px]">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-blue-600 font-bold text-sm">
                          <IdCard className="w-5 h-5" />
                          System ID Card
                        </div>
                        <p className="text-xs text-slate-400">Your official field identity card for verification checks.</p>
                        
                        <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-[11px] space-y-1 text-slate-600">
                          <div className="flex justify-between"><span>Holder:</span><span className="font-semibold text-slate-800">{activeUser.name}</span></div>
                          <div className="flex justify-between"><span>Blood Group:</span><span className="font-semibold text-red-655 text-red-600">O+</span></div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1 text-xs border-slate-200 hover:bg-slate-50"
                          onClick={() => setShowIdPreviewModal(true)}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => toast.success('Your ID Card PDF downloaded successfully!')}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> Download
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Vendor ID Card Lock */
                    <div className="p-5 border border-slate-200 rounded-2xl bg-slate-50/50 flex flex-col items-center justify-center text-center h-[280px]">
                      <Shield className="w-8 h-8 text-slate-350 mb-2" />
                      <p className="text-xs font-bold text-slate-800">ID Card Restricted</p>
                      <p className="text-[10px] text-slate-400 mt-1 max-w-[150px]">ID cards are exclusively generated for active Field Executives.</p>
                    </div>
                  )}

                  {/* Authorization Letter (FE & Vendor) */}
                  <div className="p-5 border border-slate-200 rounded-2xl bg-white shadow-xs flex flex-col justify-between h-[280px]">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                        <FileCheck className="w-5 h-5" />
                        Authorization Letter
                      </div>
                      <p className="text-xs text-slate-400">Official visitation letter authorizing background physical verifications.</p>
                      
                      <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-[11px] space-y-1 text-slate-600">
                        <div className="flex justify-between"><span>File:</span><span className="font-semibold text-slate-800 truncate max-w-[120px]">HDFC_Auth_2026.pdf</span></div>
                        <div className="flex justify-between"><span>Status:</span><span className="font-bold text-emerald-600">Active</span></div>
                      </div>
                    </div>
                    
                    <Button 
                      size="sm" 
                      className="w-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => toast.success('Authorization Letter downloaded successfully!')}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowProfileModal(false)}>
              Close Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile ID Card Preview Modal */}
      <Dialog open={showIdPreviewModal} onOpenChange={setShowIdPreviewModal}>
        <DialogContent className="bg-slate-100 border-slate-200 justify-center flex flex-col items-center max-w-sm">
          <DialogHeader>
            <DialogTitle className="sr-only">ID Preview</DialogTitle>
          </DialogHeader>
          <div className="w-[260px] h-[390px] bg-white rounded-xl shadow-2xl relative overflow-hidden border border-slate-200 mt-2">
            <div className="h-20 bg-blue-600 w-full absolute top-0 left-0" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 75%)' }}></div>
            <div className="relative z-10 flex flex-col items-center pt-6 px-5">
              <div className="w-full flex justify-between items-start mb-1">
                <span className="text-white font-extrabold text-xs tracking-wider">AD Ventures</span>
                <span className="text-white/80 text-[8px] uppercase font-bold tracking-widest">Verify ID</span>
              </div>

              <Avatar className="w-20 h-20 border-4 border-white shadow-md mb-2 bg-slate-100 mt-2">
                <AvatarImage src={activeUser.avatar} className="object-cover" />
                <AvatarFallback className="text-xl text-slate-400">{activeUser.name?.split(' ').map(n => n[0]).join('') || 'U'}</AvatarFallback>
              </Avatar>

              <h2 className="text-base font-bold text-slate-800 text-center leading-tight">{activeUser.name}</h2>
              <p className="text-[9px] font-bold text-blue-600 uppercase tracking-widest mt-0.5 mb-3">Field Executive</p>

              <div className="w-full space-y-1.5 text-[11px]">
                <div className="bg-slate-50 p-1.5 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-450 uppercase text-[8px] font-bold">Emp ID</span>
                  <span className="font-bold text-slate-800">{activeUser.id}</span>
                </div>
                <div className="bg-slate-50 p-1.5 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-450 uppercase text-[8px] font-bold">Blood Group</span>
                  <span className="font-bold text-red-600">O+</span>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 w-full p-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                <div className="w-16 h-5 bg-slate-200 rounded flex items-center justify-center text-[6px] tracking-wider text-slate-450">||| |||| || |||</div>
                <span className="text-[7px] text-slate-450">Valid: 12/2026</span>
              </div>
            </div>
          </div>
          <DialogFooter className="w-full mt-4 flex justify-center">
            <Button
              onClick={() => toast.success('Your ID Card PDF downloaded successfully!')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-1.5 text-xs py-1.5"
            >
              <Download className="w-4 h-4" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
